/**
 * ONE source for what a referral is worth on screen (L15).
 *
 * The figure is NEVER typed into copy. It is resolved at render from
 * `program_referral_settings` (via the display-only `public_referral_terms`
 * read), because a typed number is right only until the config moves — which is
 * exactly how the "$5 a card, PayPal at $50" wording survived a scheme that
 * never existed.
 *
 * COPY DISCIPLINE:
 *  - The reward is a DISCOUNT off the Cardmoji card fee. It is not cash, there
 *    is no payout, no PayPal, no minimum and no balance.
 *  - One discount applies per card ordered; discounts do not stack.
 *  - When nothing is configured, the terms sentence renders NOTHING rather than
 *    a default — no figure may appear that config does not grant.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ReferralRewardTerms {
  /** e.g. "20%" — already formatted, resolved from config. */
  inviteeLabel: string | null;
  referrerLabel: string | null;
  version: number | null;
}

function labelFor(reward: any): string | null {
  const form = String(reward?.form ?? "none");
  if (form === "percent") {
    const pct = Number(reward?.percent_off ?? 0);
    return pct > 0 ? `${pct}%` : null;
  }
  if (form === "fixed") {
    const cents = Number(reward?.amount_off_cents ?? 0);
    return cents > 0 ? `$${(cents / 100).toFixed(2)}` : null;
  }
  return null;
}

export async function fetchReferralTerms(programSlug?: string): Promise<ReferralRewardTerms | null> {
  const { data, error } = await supabase.rpc("public_referral_terms" as any, {
    p_program_slug: programSlug ?? null,
  });
  if (error || !data || typeof data !== "object") return null;
  const d = data as any;
  if (!d.configured) return null;
  const inviteeLabel = labelFor(d.invitee_reward);
  const referrerLabel = labelFor(d.referrer_reward);
  if (!inviteeLabel && !referrerLabel) return null;
  return { inviteeLabel, referrerLabel, version: Number(d.version ?? 0) || null };
}

/** Null until resolved, and null forever if nothing is configured. */
export function useReferralTerms(programSlug?: string): ReferralRewardTerms | null {
  const [terms, setTerms] = useState<ReferralRewardTerms | null>(null);
  useEffect(() => {
    let live = true;
    fetchReferralTerms(programSlug).then((t) => {
      if (live) setTerms(t);
    });
    return () => {
      live = false;
    };
  }, [programSlug]);
  return terms;
}

/**
 * Fills {{inviteeReward}} / {{referrerReward}} in a counsel-owned sentence.
 * Returns null when either token is unresolved — an unresolved money token must
 * never reach a customer.
 */
export function resolveRewardCopy(template: string, terms: ReferralRewardTerms | null): string | null {
  if (!terms) return null;
  const needsInvitee = template.includes("{{inviteeReward}}");
  const needsReferrer = template.includes("{{referrerReward}}");
  if (needsInvitee && !terms.inviteeLabel) return null;
  if (needsReferrer && !terms.referrerLabel) return null;
  return template
    .replace(/\{\{inviteeReward\}\}/g, terms.inviteeLabel ?? "")
    .replace(/\{\{referrerReward\}\}/g, terms.referrerLabel ?? "");
}
